import { format, parseISO } from "date-fns";

// ─── Currency ─────────────────────────────────────────────────────────────────

export function formatCurrency(
  amount: number,
  currency = "INR",
  locale = "en-IN",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

// ─── Dates ────────────────────────────────────────────────────────────────────

export function formatDate(date: string | Date, fmt = "dd MMM yyyy"): string {
  const d = typeof date === "string" ? parseISO(date) : date;
  return format(d, fmt);
}

export function formatDateRange(checkIn: string, checkOut: string): string {
  return `${formatDate(checkIn, "dd MMM")} → ${formatDate(checkOut, "dd MMM yyyy")}`;
}

export function nightsBetween(checkIn: string, checkOut: string): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round(
    (new Date(checkOut).getTime() - new Date(checkIn).getTime()) / msPerDay,
  );
}

// ─── GST ─────────────────────────────────────────────────────────────────────

/**
 * Returns the applicable GST rate based on room tariff per night.
 * < ₹7,500  → 5%
 * ≥ ₹7,500  → 18%
 */
export function getGstRate(pricePerNight: number): number {
  return pricePerNight < 7500 ? 5 : 18;
}

/**
 * Returns { gstRate, gstAmount, total } for a given subtotal.
 * pricePerNight is used only to determine the rate tier.
 */
export function calculateGst(
  subtotal: number,
  pricePerNight: number,
): { gstRate: number; gstAmount: number; total: number } {
  const gstRate = getGstRate(pricePerNight);
  const gstAmount = Math.round((subtotal * gstRate) / 100);
  return { gstRate, gstAmount, total: subtotal + gstAmount };
}

// ─── Area ─────────────────────────────────────────────────────────────────────

export function sqftToSqm(sqft: number): number {
  return Math.round(sqft * 0.0929);
}

export function formatArea(sqft: number): string {
  return `${sqft} sq ft (${sqftToSqm(sqft)} m²)`;
}

// ─── Slugs ────────────────────────────────────────────────────────────────────

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ─── File size ────────────────────────────────────────────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Misc ─────────────────────────────────────────────────────────────────────

export function truncate(str: string, max = 80): string {
  return str.length <= max ? str : str.slice(0, max).trimEnd() + "…";
}

export function initials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}